/**
 * The points an operator is placing or holding: an unfinished zone's corners,
 * and the grab handles of the zone selected for editing.
 *
 * The predecessor placed a polygon's corners and never drew them, so there was
 * no way to see where a click had landed or which corner would close the ring.
 * Corners are drawn numbered, in the order the fence will carry them, and the
 * first one grows once there are three, because clicking it closes the shape.
 */

import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { zoneHandles } from '@/map/zoneEdit';
import type { Zone } from '@/stores/missionStore';
import { useEffect, useRef } from 'react';

export const ZONE_EDIT_SOURCE_ID = 'zone-edit';
/** Queried on mousedown to find which handle is under the cursor. */
export const ZONE_HANDLE_LAYER = 'zone-edit-handles';
const ZONE_EDIT_LINE_LAYER = 'zone-edit-line';
const ZONE_EDIT_LABEL_LAYER = 'zone-edit-labels';

/**
 * Everything this layer adds to a map. Planning only, on purpose: these are
 * the editor's grab points, not part of the picture, and the commander sees the
 * zone itself through the zone layer.
 */
export const ZONE_EDIT_MAP_IDS: readonly string[] = [
  ZONE_EDIT_SOURCE_ID,
  ZONE_EDIT_LINE_LAYER,
  ZONE_HANDLE_LAYER,
  ZONE_EDIT_LABEL_LAYER,
];

export interface ZoneEditView {
  /** A shape being drawn, corner by corner. */
  draw: {
    kind: 'keep-in' | 'keep-out';
    shape: 'circle' | 'rect' | 'polygon';
    points: ReadonlyArray<{ lat: number; lon: number }>;
  } | null;
  /** The zone whose handles are offered for dragging. */
  selected: Zone | null;
}

function colorFor(kind: 'keep-in' | 'keep-out'): string {
  return kind === 'keep-out' ? '#f0563f' : '#4ec9a3';
}

interface Feature {
  type: 'Feature';
  properties: Record<string, string | number | boolean>;
  geometry:
    | { type: 'Point'; coordinates: [number, number] }
    | { type: 'LineString'; coordinates: Array<[number, number]> };
}

export function zoneEditFeatures(view: ZoneEditView): Feature[] {
  const features: Feature[] = [];
  const { draw, selected } = view;

  if (draw && draw.points.length) {
    const color = colorFor(draw.kind);
    if (draw.shape === 'polygon' && draw.points.length > 1) {
      features.push({
        type: 'Feature',
        properties: { color },
        geometry: {
          type: 'LineString',
          coordinates: draw.points.map((p) => [p.lon, p.lat] as [number, number]),
        },
      });
    }
    draw.points.forEach((point, index) => {
      const closes = draw.shape === 'polygon' && index === 0 && draw.points.length >= 3;
      features.push({
        type: 'Feature',
        properties: {
          role: 'draft',
          index,
          label: draw.shape === 'polygon' ? String(index + 1) : '',
          color,
          radius: closes ? 9 : 6,
          closes,
        },
        geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
      });
    });
  }

  if (selected) {
    const color = colorFor(selected.kind);
    for (const handle of zoneHandles(selected)) {
      features.push({
        type: 'Feature',
        properties: {
          role: handle.role,
          index: handle.index,
          label: handle.label,
          zoneId: selected.zoneId,
          color,
          radius: handle.role === 'centre' ? 5 : 7,
          closes: false,
        },
        geometry: { type: 'Point', coordinates: [handle.lon, handle.lat] },
      });
    }
  }

  return features;
}

export interface ZoneEditLayer {
  update: (view: ZoneEditView) => void;
  destroy: () => void;
}

export function createZoneEditLayer(map: MapLike): ZoneEditLayer {
  if (!map.getSource(ZONE_EDIT_SOURCE_ID)) {
    map.addSource(ZONE_EDIT_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(ZONE_EDIT_LINE_LAYER)) {
    map.addLayer({
      id: ZONE_EDIT_LINE_LAYER,
      type: 'line',
      source: ZONE_EDIT_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-dasharray': [2, 2] },
    });
  }
  if (!map.getLayer(ZONE_HANDLE_LAYER)) {
    map.addLayer({
      id: ZONE_HANDLE_LAYER,
      type: 'circle',
      source: ZONE_EDIT_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': ['get', 'radius'],
        'circle-color': '#0c0c0d',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': ['case', ['get', 'closes'], 3, 2],
      },
    });
  }
  if (!map.getLayer(ZONE_EDIT_LABEL_LAYER)) {
    map.addLayer({
      id: ZONE_EDIT_LABEL_LAYER,
      type: 'symbol',
      source: ZONE_EDIT_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
  }

  return {
    update: (view) => {
      geoJsonSource(map, ZONE_EDIT_SOURCE_ID)?.setData({
        type: 'FeatureCollection',
        features: zoneEditFeatures(view),
      });
    },
    destroy: () => {
      for (const id of [ZONE_EDIT_LABEL_LAYER, ZONE_HANDLE_LAYER, ZONE_EDIT_LINE_LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(ZONE_EDIT_SOURCE_ID)) map.removeSource(ZONE_EDIT_SOURCE_ID);
    },
  };
}

/** `view` should keep its identity between renders that change nothing. */
export function useZoneEditLayer(view: ZoneEditView): void {
  const controller = useMapController();
  const layerRef = useRef<ZoneEditLayer | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createZoneEditLayer(map);
        layer.update(viewRef.current);
        layerRef.current = layer;
        return () => {
          layer.destroy();
          layerRef.current = null;
        };
      }),
    [controller],
  );

  useEffect(() => {
    layerRef.current?.update(view);
  }, [view]);
}
